import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../layout/Layout';
import { NiceSignInFeatureIcon } from './NiceSignInFeatureIcon';
import { NICE_SIGN_IN_FEATURE_IDS, NICE_SIGN_IN_TIP_IDS } from './nice-sign-in-features';
import './NiceSignInScreen.css';

interface NiceSignInScreenProps {
  onSignInWithNice: () => Promise<void>;
  error: string | null;
}

export function NiceSignInScreen({ onSignInWithNice, error }: NiceSignInScreenProps) {
  const { t } = useTranslation();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.classList.add('nice-sign-in-active');
    return () => document.documentElement.classList.remove('nice-sign-in-active');
  }, []);

  const handleSignIn = async () => {
    setLocalError(null);
    setIsSigningIn(true);
    try {
      await onSignInWithNice();
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : t('auth:niceSignIn.failed'));
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <Layout scrollable>
      <div className="nice-sign-in-screen">
        <div className="nice-sign-in-screen__content">
          <div className="page-card page-card--narrow nice-sign-in-card">
            <img src="/assets/nice-cxone-logo.png" alt="NiCE CXone" className="nice-sign-in-logo" />
            <p className="nice-sign-in-description">{t('auth:niceSignIn.description')}</p>

            <div className="nice-sign-in-actions">
              {(localError || error) && <div className="error-message">{localError || error}</div>}

              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={handleSignIn}
                disabled={isSigningIn}
              >
                {isSigningIn ? t('auth:niceSignIn.signingIn') : t('auth:niceSignIn.signInButton')}
              </button>
            </div>
          </div>

          <section className="nice-sign-in-features" aria-labelledby="nice-sign-in-features-title">
            <h2 id="nice-sign-in-features-title" className="nice-sign-in-section-title">
              {t('auth:niceSignIn.featuresTitle')}
            </h2>
            <ul className="nice-sign-in-feature-grid">
              {NICE_SIGN_IN_FEATURE_IDS.map((featureId) => (
                <li key={featureId} className="nice-sign-in-feature-card">
                  <div className="nice-sign-in-feature-header">
                    <span className="nice-sign-in-feature-icon">
                      <NiceSignInFeatureIcon featureId={featureId} />
                    </span>
                    <h3>{t(`auth:niceSignIn.features.${featureId}.title`)}</h3>
                  </div>
                  <p>{t(`auth:niceSignIn.features.${featureId}.description`)}</p>
                </li>
              ))}
            </ul>
          </section>

          <section className="nice-sign-in-tips" aria-labelledby="nice-sign-in-tips-title">
            <h2 id="nice-sign-in-tips-title" className="nice-sign-in-section-title">
              {t('auth:niceSignIn.tipsTitle')}
            </h2>
            <ul className="nice-sign-in-tips-list">
              {NICE_SIGN_IN_TIP_IDS.map((tipId) => (
                <li key={tipId}>{t(`auth:niceSignIn.tips.${tipId}`)}</li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </Layout>
  );
}
